import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { VALUE_CHAIN_GRAPHS } from '@/data/valueChain/starlinkGraphs';
import {
  BomView,
  ChainView,
  DirectoryView,
  ExposureView,
  ManufacturingView,
  OpportunitiesView,
} from '@/components/value-chain/explorer/CatalogViews';
import { ExplorerSidebar } from '@/components/value-chain/explorer/ExplorerSidebar';
import { ExplorerTopBar } from '@/components/value-chain/explorer/ExplorerTopBar';
import { GraphWorkspace } from '@/components/value-chain/explorer/GraphWorkspace';
import { HierarchyWorkspace } from '@/components/value-chain/explorer/HierarchyWorkspace';
import { RevenueMapView } from '@/components/value-chain/explorer/RevenueMapView';
import { WorkspaceTabs } from '@/components/value-chain/explorer/WorkspaceTabs';
import { shortName } from '@/components/value-chain/nodeVisuals';
import { isSpcxGraph } from '@/components/value-chain/productRevenue';
import { ancestorsOf, buildTreeIndex, childrenOf } from '@/components/value-chain/treeIndex';
import { valueChainService } from '@/services/api';
import type {
  ValueChainDetailsTab,
  ValueChainEdge,
  ValueChainGraph,
  ValueChainInspectorTab,
  ValueChainNavId,
  ValueChainNode,
  ValueChainNodeType,
  ValueChainRecentItem,
  ValueChainRelationType,
  ValueChainWorkspaceTab,
} from '@/types/valueChain';

const STORAGE_KEY = 'value-chain-starlink-v1';
const RECENT_KEY = 'value-chain-recent-v1';
const STAR_KEY = 'value-chain-stars-v1';

type PersistedState = {
  graphs: ValueChainGraph[];
  notes: Record<string, string>;
};

const TICKER_LABELS: Record<string, string> = {
  SPCX: 'SpaceX',
};

function cloneGraphs(): ValueChainGraph[] {
  return JSON.parse(JSON.stringify(VALUE_CHAIN_GRAPHS)) as ValueChainGraph[];
}

function loadState(): { graphs: ValueChainGraph[]; notes: Record<string, string> } {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { graphs: cloneGraphs(), notes: {} };
    const parsed = JSON.parse(raw) as PersistedState;
    if (!Array.isArray(parsed.graphs) || parsed.graphs.length === 0) {
      return { graphs: cloneGraphs(), notes: parsed.notes ?? {} };
    }
    return { graphs: parsed.graphs, notes: parsed.notes ?? {} };
  } catch {
    return { graphs: cloneGraphs(), notes: {} };
  }
}

function loadRecent(): ValueChainRecentItem[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as ValueChainRecentItem[];
    return Array.isArray(parsed) ? parsed.slice(0, 8) : [];
  } catch {
    return [];
  }
}

function loadStars(): string[] {
  try {
    const raw = localStorage.getItem(STAR_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as string[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function productCrumbs(graph: ValueChainGraph): Array<{ label: string }> {
  const crumbs = [{ label: 'Products' }];
  if (graph.ticker) crumbs.push({ label: TICKER_LABELS[graph.ticker] ?? graph.ticker });
  if (/starlink/i.test(graph.name)) {
    crumbs.push({ label: 'Starlink' });
    const rest = graph.name.replace(/starlink\s*/i, '').trim();
    if (rest) crumbs.push({ label: rest });
  } else {
    crumbs.push({ label: graph.name });
  }
  return crumbs;
}

export function ValueChainApp() {
  const navigate = useNavigate();
  const [{ graphs: initialGraphs, notes: initialNotes }] = useState(loadState);
  const [savedGraphs, setGraphs] = useState(initialGraphs);
  const [liveGraphs, setLiveGraphs] = useState<ValueChainGraph[]>([]);
  const [notes, setNotes] = useState(initialNotes);
  const [graphId, setGraphId] = useState(VALUE_CHAIN_GRAPHS[0].id);
  const [navId, setNavId] = useState<ValueChainNavId>('explore');
  const [workspaceTab, setWorkspaceTab] = useState<ValueChainWorkspaceTab>('hierarchy');
  const [fullGraph, setFullGraph] = useState(false);
  const [detailsTab, setDetailsTab] = useState<ValueChainDetailsTab>('details');
  const [inspectorTab, setInspectorTab] = useState<ValueChainInspectorTab>('overview');
  const [showGraphMenu, setShowGraphMenu] = useState(false);
  const [query, setQuery] = useState('');
  const [recent, setRecent] = useState(loadRecent);
  const [starredIds, setStarredIds] = useState(() => new Set(loadStars()));
  const [editing, setEditing] = useState(false);
  const [draftDescription, setDraftDescription] = useState('');
  const [modal, setModal] = useState<'node' | 'relation' | null>(null);
  const [revenueFocus, setRevenueFocus] = useState<string | null>(null);
  const [revenueReset, setRevenueReset] = useState(0);
  const [nodeDraft, setNodeDraft] = useState({
    name: '',
    type: 'Component' as ValueChainNodeType,
    description: '',
  });
  const [relationDraft, setRelationDraft] = useState({
    from: '',
    to: '',
    relation: 'HAS_PART' as ValueChainRelationType,
  });

  const graphs = useMemo(() => {
    if (!liveGraphs.length) return savedGraphs;
    const liveIds = new Set(liveGraphs.map((item) => item.id));
    return [...liveGraphs, ...savedGraphs.filter((item) => !liveIds.has(item.id))];
  }, [liveGraphs, savedGraphs]);

  useEffect(() => {
    let cancelled = false;
    const loadLive = async () => {
      try {
        const { products } = await valueChainService.listProducts();
        const loaded = await Promise.all(
          products.map(async (product) => {
            try {
              return await valueChainService.getGraph(product.ticker, product.product_slug);
            } catch {
              return null;
            }
          }),
        );
        if (cancelled) return;
        setLiveGraphs(loaded.filter((item): item is ValueChainGraph => Boolean(item)));
      } catch {
        if (!cancelled) setLiveGraphs([]);
      }
    };
    void loadLive();
    const timer = window.setInterval(() => {
      void loadLive();
    }, 15000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, []);

  const preferredLive = useRef(false);
  useEffect(() => {
    if (!liveGraphs.length || preferredLive.current) return;
    preferredLive.current = true;
    setGraphId(liveGraphs[0].id);
  }, [liveGraphs]);

  const graph = graphs.find((item) => item.id === graphId) ?? graphs[0];
  const index = useMemo(() => buildTreeIndex(graph), [graph]);
  const [selectedId, setSelectedId] = useState(graph.rootId);
  const [focusId, setFocusId] = useState(graph.rootId);
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set([graph.rootId]));

  const resolvedSelectedId = index.byId.has(selectedId) ? selectedId : graph.defaultSelectedId;
  const resolvedFocusId = index.byId.has(focusId) ? focusId : graph.rootId;
  const selected = index.byId.get(resolvedSelectedId) ?? index.byId.get(graph.rootId) ?? graph.nodes[0];
  const focus = index.byId.get(resolvedFocusId) ?? selected;
  const root = index.byId.get(graph.rootId) ?? selected;

  useEffect(() => {
    setSelectedId(graph.rootId);
    setFocusId(graph.rootId);
    setExpanded(new Set([graph.rootId]));
    setEditing(false);
    setFullGraph(false);
  }, [graph.id, graph.rootId]);

  useEffect(() => {
    if (!selected) return;
    const next: ValueChainRecentItem = {
      id: selected.id,
      name: selected.name,
      type: selected.type,
      graphId: graph.id,
    };
    setRecent((prev) => {
      const filtered = prev.filter((item) => item.id !== next.id);
      const merged = [next, ...filtered].slice(0, 8);
      localStorage.setItem(RECENT_KEY, JSON.stringify(merged));
      return merged;
    });
  }, [graph.id, selected.id, selected.name, selected.type]);

  const searchHits = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return graph.nodes
      .filter((node) =>
        [node.name, node.type, node.description, node.manufacturer, node.company]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(q),
      )
      .slice(0, 20);
  }, [graph.nodes, query]);

  const showGraph = navId === 'graph' || workspaceTab === 'graph' || fullGraph;
  const focusedGraph = showGraph && navId !== 'graph' && !fullGraph;

  const updateGraph = (updater: (current: ValueChainGraph) => ValueChainGraph) => {
    if (liveGraphs.some((item) => item.id === graph.id)) {
      setLiveGraphs((prev) => prev.map((item) => (item.id === graph.id ? updater(item) : item)));
      return;
    }
    setGraphs((prev) => prev.map((item) => (item.id === graph.id ? updater(item) : item)));
  };

  const revealNode = (id: string, drill = false) => {
    const parentId = index.parent.get(id);
    const nextFocus = drill ? id : (parentId ?? id);
    setSelectedId(id);
    setFocusId(nextFocus);
    const path = ancestorsOf(index, id).map((node) => node.id);
    setExpanded((prev) => {
      const next = new Set(prev);
      path.forEach((nodeId) => next.add(nodeId));
      if (parentId) next.add(parentId);
      next.add(graph.rootId);
      return next;
    });
    setInspectorTab('overview');
    setEditing(false);
  };

  const handleNav = (id: ValueChainNavId) => {
    if (id === 'home') {
      navigate('/dashboard');
      return;
    }
    setNavId(id);
    setFullGraph(id === 'graph');
    if (id === 'explore') setWorkspaceTab('hierarchy');
    if (id === 'revenue-map') setWorkspaceTab('revenue-map');
    if (id === 'chains') setWorkspaceTab('chain');
    if (id === 'opportunities') setWorkspaceTab('opportunities');
    if (id === 'manufacturing') setWorkspaceTab('manufacturing');
    if (id === 'graph') setWorkspaceTab('graph');
  };

  const handleWorkspaceTab = (tab: ValueChainWorkspaceTab) => {
    setWorkspaceTab(tab);
    setFullGraph(false);
    if (tab === 'graph') setNavId('explore');
    else if (tab === 'revenue-map') setNavId('revenue-map');
    else if (tab === 'chain') setNavId('chains');
    else if (tab === 'opportunities') setNavId('opportunities');
    else if (tab === 'manufacturing') setNavId('manufacturing');
    else setNavId('explore');
  };

  const handleToggleStar = () => {
    if (!selected) return;
    setStarredIds((prev) => {
      const next = new Set(prev);
      if (next.has(selected.id)) next.delete(selected.id);
      else next.add(selected.id);
      localStorage.setItem(STAR_KEY, JSON.stringify([...next]));
      return next;
    });
  };

  const handleAddNode = () => {
    const name = nodeDraft.name.trim();
    if (!name) return;
    const id = `${name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${Date.now()}`;
    const newNode: ValueChainNode = {
      id,
      name,
      type: nodeDraft.type,
      description: nodeDraft.description.trim() || 'Added in Value Chain Explorer.',
    };
    updateGraph((current) => ({
      ...current,
      nodes: [...current.nodes, newNode],
      edges:
        selectedId && selectedId !== id
          ? [
              ...current.edges,
              {
                id: `${selectedId}__HAS_PART__${id}`,
                from: selectedId,
                to: id,
                relation: 'HAS_PART',
              },
            ]
          : current.edges,
    }));
    revealNode(id);
    setModal(null);
    setNodeDraft({ name: '', type: 'Component', description: '' });
  };

  const handleAddRelation = () => {
    if (!relationDraft.from || !relationDraft.to || relationDraft.from === relationDraft.to) return;
    const newEdge: ValueChainEdge = {
      id: `${relationDraft.from}__${relationDraft.relation}__${relationDraft.to}`,
      from: relationDraft.from,
      to: relationDraft.to,
      relation: relationDraft.relation,
    };
    updateGraph((current) => ({
      ...current,
      edges: current.edges.some((edge) => edge.id === newEdge.id)
        ? current.edges
        : [...current.edges, newEdge],
    }));
    setModal(null);
  };

  const handleSaveEdit = () => {
    updateGraph((current) => ({
      ...current,
      nodes: current.nodes.map((node) =>
        node.id === selectedId ? { ...node, description: draftDescription } : node,
      ),
    }));
    setEditing(false);
  };

  const directoryNodes = (kind: 'companies' | 'materials' | 'customers'): ValueChainNode[] => {
    if (kind === 'companies') {
      return graph.nodes.filter((node) => node.type === 'Company').slice(0, 120);
    }
    if (kind === 'materials') {
      return graph.nodes
        .filter((node) => node.type === 'Material' || node.type === 'Chemical / Dopant')
        .slice(0, 120);
    }
    return graph.nodes
      .filter((node) => node.type !== 'Company' && (index.children.get(node.id)?.length ?? 0) > 0)
      .slice(0, 120);
  };

  if (!selected || !focus || !root) return null;

  const recentNodes = recent
    .map((item) => index.byId.get(item.id))
    .filter((node): node is ValueChainNode => Boolean(node));

  const searchOpen = query.trim().length > 1;

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50 text-gray-900">
      <ExplorerSidebar
        active={navId}
        onNavigate={handleNav}
        recent={recent}
        recentNodes={recentNodes}
        onSelectRecent={(id) => {
          revealNode(id);
          setNavId('explore');
          setWorkspaceTab('hierarchy');
          setFullGraph(false);
        }}
        searchQuery={query}
        onSearchQueryChange={setQuery}
        onSubmitSearch={() => {
          if (searchHits[0]) {
            revealNode(searchHits[0].id);
            setQuery('');
          }
        }}
      />

      <div className="flex min-w-0 flex-1 flex-col">
        {revenueFocus === 'Starlink' ? null : (
        <ExplorerTopBar
          graph={graph}
          graphs={graphs}
          crumbs={
            navId === 'revenue-map' || workspaceTab === 'revenue-map'
              ? [
                  { label: 'Companies', onClick: () => handleNav('companies') },
                  {
                    label:
                      TICKER_LABELS[graph.ticker ?? ''] ??
                      (isSpcxGraph(graph) ? 'SpaceX' : graph.name),
                    onClick: () => {
                      setRevenueFocus(null);
                      setRevenueReset((value) => value + 1);
                    },
                  },
                  ...(revenueFocus ? [{ label: revenueFocus }] : []),
                ]
              : productCrumbs(graph)
          }
          query={query}
          onQueryChange={setQuery}
          onSubmitSearch={() => {
            if (searchHits[0]) {
              revealNode(searchHits[0].id);
              setQuery('');
            }
          }}
          searchOpen={searchOpen}
          searchHits={searchHits.map((node) => ({
            id: node.id,
            name: shortName(node.name),
            type: node.type,
          }))}
          onPickSearch={(id) => {
            revealNode(id);
            setQuery('');
            setNavId('explore');
            setWorkspaceTab('hierarchy');
            setFullGraph(false);
          }}
          onGraphChange={(id) => {
            setGraphId(id);
            setShowGraphMenu(false);
          }}
          showGraphMenu={showGraphMenu}
          onShowGraphMenu={setShowGraphMenu}
          partCount={graph.nodes.length}
          mappedRelationships={index.mappedRelationships}
          maxDepth={index.maxDepth}
          compact={navId === 'revenue-map' || workspaceTab === 'revenue-map'}
          hideCrumbs={Boolean(revenueFocus)}
        />
        )}

        {navId === 'revenue-map' || workspaceTab === 'revenue-map' ? null : (
          <WorkspaceTabs
            active={
              showGraph
                ? 'graph'
                : navId === 'companies' || navId === 'materials' || navId === 'customers'
                  ? null
                  : workspaceTab
            }
            onChange={handleWorkspaceTab}
          />
        )}

        {showGraph ? (
          <GraphWorkspace
            graphs={graphs}
            graph={graph}
            index={index}
            selectedId={resolvedSelectedId}
            selected={selected}
            onSelect={(id) => revealNode(id)}
            onGraphChange={setGraphId}
            detailsTab={detailsTab}
            onDetailsTabChange={setDetailsTab}
            note={notes[resolvedSelectedId] ?? ''}
            onNoteChange={(value) => setNotes((prev) => ({ ...prev, [resolvedSelectedId]: value }))}
            editing={editing}
            draftDescription={draftDescription}
            onToggleEdit={() => {
              setDraftDescription(selected.description);
              setEditing(true);
            }}
            onDraftChange={setDraftDescription}
            onSaveEdit={handleSaveEdit}
            onAddRelation={() => {
              setRelationDraft((prev) => ({ ...prev, from: resolvedSelectedId || prev.from }));
              setModal('relation');
            }}
            focused={focusedGraph}
            onOpenFullGraph={() => {
              setNavId('graph');
              setFullGraph(true);
              setWorkspaceTab('graph');
            }}
          />
        ) : navId === 'companies' ? (
          <DirectoryView
            title="Companies"
            subtitle="Mapped manufacturers and inferred suppliers in this product graph."
            nodes={directoryNodes('companies')}
            onSelect={(id) => revealNode(id)}
          />
        ) : navId === 'materials' ? (
          <DirectoryView
            title="Materials"
            subtitle="Material and chemical nodes compiled from hop research."
            nodes={directoryNodes('materials')}
            onSelect={(id) => revealNode(id)}
          />
        ) : navId === 'customers' ? (
          <DirectoryView
            title="Customers"
            subtitle="Parent assemblies that consume mapped parts — the demand side of this chain."
            nodes={directoryNodes('customers')}
            onSelect={(id) => revealNode(id, true)}
          />
        ) : navId === 'revenue-map' || workspaceTab === 'revenue-map' ? (
          <RevenueMapView
            graph={graph}
            graphs={graphs}
            index={index}
            root={root}
            onSelect={(id) => revealNode(id)}
            onOpenGraph={setGraphId}
            onFocusChange={setRevenueFocus}
            resetNonce={revenueReset}
            onViewDetails={(id) => {
              revealNode(id);
              setNavId('explore');
              setWorkspaceTab('hierarchy');
              setFullGraph(false);
            }}
          />
        ) : workspaceTab === 'chain' ? (
          <ChainView
            graph={graph}
            index={index}
            selected={selected}
            onSelect={(id) => revealNode(id)}
          />
        ) : workspaceTab === 'opportunities' ? (
          <OpportunitiesView
            graph={graph}
            index={index}
            selected={selected}
            onSelect={(id) => revealNode(id)}
          />
        ) : workspaceTab === 'exposure' ? (
          <ExposureView index={index} selected={selected} />
        ) : workspaceTab === 'manufacturing' ? (
          <ManufacturingView index={index} selected={selected} onSelect={(id) => revealNode(id)} />
        ) : workspaceTab === 'bom' ? (
          <BomView index={index} selected={selected} onSelect={(id) => revealNode(id)} />
        ) : (
          <HierarchyWorkspace
            graph={graph}
            index={index}
            root={root}
            focus={focus}
            selected={selected}
            expanded={expanded}
            inspectorTab={inspectorTab}
            starred={starredIds.has(selected.id)}
            onToggleExpanded={(id) => {
              setExpanded((prev) => {
                const next = new Set(prev);
                if (next.has(id)) next.delete(id);
                else next.add(id);
                return next;
              });
            }}
            onExpandAll={() => {
              const next = new Set([graph.rootId]);
              childrenOf(index, graph.rootId).forEach((child) => {
                next.add(child.id);
                childrenOf(index, child.id).forEach((grand) => next.add(grand.id));
              });
              setExpanded(next);
            }}
            onFocus={(id) => revealNode(id, true)}
            onSelect={(id) => revealNode(id)}
            onDrill={(id) => revealNode(id, true)}
            onInspectorTabChange={setInspectorTab}
            onToggleStar={handleToggleStar}
          />
        )}
      </div>

      {modal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-sm">
            <h3 className="text-lg font-semibold">
              {modal === 'node' ? 'Add Node' : 'Add Relation'}
            </h3>
            {modal === 'node' ? (
              <div className="mt-4 space-y-3">
                <input
                  value={nodeDraft.name}
                  onChange={(event) => setNodeDraft((prev) => ({ ...prev, name: event.target.value }))}
                  placeholder="Name"
                  className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none focus:border-gray-400"
                />
                <select
                  value={nodeDraft.type}
                  onChange={(event) =>
                    setNodeDraft((prev) => ({
                      ...prev,
                      type: event.target.value as ValueChainNodeType,
                    }))
                  }
                  className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none focus:border-gray-400"
                >
                  {['Product / System', 'Assembly / Module', 'Component', 'Device', 'Material', 'Company'].map(
                    (type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ),
                  )}
                </select>
                <textarea
                  value={nodeDraft.description}
                  onChange={(event) =>
                    setNodeDraft((prev) => ({ ...prev, description: event.target.value }))
                  }
                  placeholder="Description"
                  rows={4}
                  className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none focus:border-gray-400"
                />
              </div>
            ) : (
              <div className="mt-4 space-y-3">
                <select
                  value={relationDraft.from}
                  onChange={(event) =>
                    setRelationDraft((prev) => ({ ...prev, from: event.target.value }))
                  }
                  className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none focus:border-gray-400"
                >
                  <option value="">From</option>
                  {graph.nodes.slice(0, 400).map((node) => (
                    <option key={node.id} value={node.id}>
                      {node.name}
                    </option>
                  ))}
                </select>
                <select
                  value={relationDraft.relation}
                  onChange={(event) =>
                    setRelationDraft((prev) => ({
                      ...prev,
                      relation: event.target.value as ValueChainRelationType,
                    }))
                  }
                  className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none focus:border-gray-400"
                >
                  {['HAS_PART', 'COMPOSED_OF', 'MANUFACTURED_BY', 'SUPPLIES'].map((relation) => (
                    <option key={relation} value={relation}>
                      {relation}
                    </option>
                  ))}
                </select>
                <select
                  value={relationDraft.to}
                  onChange={(event) =>
                    setRelationDraft((prev) => ({ ...prev, to: event.target.value }))
                  }
                  className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none focus:border-gray-400"
                >
                  <option value="">To</option>
                  {graph.nodes.slice(0, 400).map((node) => (
                    <option key={node.id} value={node.id}>
                      {node.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setModal(null)}
                className="rounded-lg px-3 py-2 text-sm text-gray-500 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={modal === 'node' ? handleAddNode : handleAddRelation}
                className="rounded-lg bg-gray-900 px-3 py-2 text-sm font-medium text-white"
              >
                Add
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
